import { useState, useEffect, useRef, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { m } from "framer-motion";
import { supabase } from "../../lib/supabase";
import { useStoreSettings } from "../../contexts/StoreSettingsContext";
import { useViewportAnimationTrigger } from "../../hooks/useViewportAnimationTrigger";
import { pageFade } from "../../components/animations/menuAnimations";
import { logger } from "../../utils/logger";
import ConfirmationModal from "../../components/ui/ConfirmationModal";

interface FormData {
  show_home_ambience_uploader: boolean;
  show_theme_toggle: boolean;
  show_public_reviews: boolean;
  show_home_testimonials: boolean;
  show_chefs_picks: boolean;
  scroll_thumb_brightness: number;
}

function AdminHomePageControls(): JSX.Element {
  const navigate = useNavigate();
  const containerRef = useViewportAnimationTrigger();
  const {
    settings,
    loading: contextLoading,
    updateSettings,
  } = useStoreSettings();
  const [isAdmin, setIsAdmin] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    show_home_ambience_uploader: false,
    show_theme_toggle: false,
    show_public_reviews: false,
    show_home_testimonials: true,
    show_chefs_picks: false,
    scroll_thumb_brightness: 0.6,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const isInitialLoadRef = useRef(true);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async (): Promise<void> => {
      setVerifying(true);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setError("Log in to access admin tools.");
          setIsAdmin(false);
          navigate("/login");
          return;
        }

        const { data, error: customerError } = await supabase
          .from("customers")
          .select("is_admin")
          .eq("id", user.id)
          .single();

        const customerData = data as { is_admin?: boolean } | null;
        if (customerError || !customerData?.is_admin) {
          setError("Access denied. Administrator role required.");
          setIsAdmin(false);
          navigate("/admin");
          return;
        }

        setIsAdmin(true);
        setError("");
      } catch (err) {
        logger.error(err);
        setError("Unable to verify admin permissions.");
        setIsAdmin(false);
        navigate("/admin");
      } finally {
        setVerifying(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  // Load settings into form when they're available - ONLY ON INITIAL LOAD
  // This prevents automatic changes after admin has manually set toggles
  useEffect(() => {
    if (settings && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setFormData({
        show_home_ambience_uploader:
          settings.show_home_ambience_uploader ?? false,
        show_theme_toggle: settings.show_theme_toggle ?? false,
        show_public_reviews: settings.show_public_reviews ?? false,
        // Use saved value only - no automatic logic
        show_home_testimonials: settings.show_home_testimonials ?? false,
        show_chefs_picks: settings.show_chefs_picks ?? false,
        scroll_thumb_brightness: settings.scroll_thumb_brightness ?? 0.6,
      });
    }
  }, [settings]);

  // Track scroll thumb brightness changes (no longer auto-saves)
  useEffect(() => {
    if (isInitialLoadRef.current || !settings) {
      return;
    }

    const currentValue = formData.scroll_thumb_brightness ?? 0.6;
    const savedValue = settings.scroll_thumb_brightness ?? 0.6;

    if (Math.abs(currentValue - savedValue) > 0.001) {
      setHasUnsavedChanges(true);
    }
  }, [formData.scroll_thumb_brightness, settings]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type } = e.target;

    if (type === "range") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? 0.6 : parseFloat(value),
      }));
      setHasUnsavedChanges(true);
    }
  };

  const handleToggle = (field: keyof FormData): void => {
    const nextValue = !formData[field];
    setFormData((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError("");
    setSuccess(false);

    try {
      const updates: Record<string, unknown> = {
        show_home_ambience_uploader: formData.show_home_ambience_uploader,
        show_theme_toggle: formData.show_theme_toggle,
        show_public_reviews: formData.show_public_reviews,
        show_home_testimonials: formData.show_home_testimonials,
        show_chefs_picks: formData.show_chefs_picks,
        scroll_thumb_brightness: Math.max(
          0.05,
          Math.min(
            1,
            Number((formData.scroll_thumb_brightness ?? 0.6).toFixed(2)),
          ),
        ),
      };

      // Add timestamp if reviews visibility changed
      if (
        settings &&
        formData.show_public_reviews !== (settings.show_public_reviews ?? false)
      ) {
        updates.reviews_visibility_updated_at = new Date().toISOString();
      }

      const result = await updateSettings(updates);

      if (result.success) {
        setSuccess(true);
        setHasUnsavedChanges(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setSaveError(result.error || "Failed to save settings");
      }
    } catch (err) {
      logger.error("Error saving settings:", err);
      setSaveError(
        err instanceof Error ? err.message : "Failed to save settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (): void => {
    if (settings) {
      setFormData({
        show_home_ambience_uploader:
          settings.show_home_ambience_uploader ?? false,
        show_theme_toggle: settings.show_theme_toggle ?? false,
        show_public_reviews: settings.show_public_reviews ?? false,
        show_home_testimonials: settings.show_home_testimonials ?? false,
        show_chefs_picks: settings.show_chefs_picks ?? false,
        scroll_thumb_brightness: settings.scroll_thumb_brightness ?? 0.6,
      });
      setHasUnsavedChanges(false);
      setSaveError("");
      setSuccess(false);
    }
  };

  if (verifying || contextLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-red-500/30 bg-red-500/10 p-8 text-center shadow-[0_25px_60px_-45px_rgba(248,113,113,0.6)]">
        <h2 className="text-2xl font-semibold mb-2 text-[var(--text-main)]">
          Admin Access Required
        </h2>
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <m.main
      ref={containerRef}
      className="w-full bg-[var(--bg-main)] text-[var(--text-main)]"
      variants={pageFade}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{
        pointerEvents: "auto",
        // Add padding to match .app-container spacing (prevents sections from touching viewport edges)
        paddingLeft: "clamp(1rem, 3vw, 3.5rem)",
        paddingRight: "clamp(1rem, 3vw, 3.5rem)",
        // Ensure no overflow constraints that break positioning
        overflow: "visible",
        overflowX: "visible",
        overflowY: "visible",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-3 sm:py-4 md:py-5">
        <header
          className="mb-12 flex flex-col gap-3 sm:gap-4 md:gap-6 md:flex-row md:items-end md:justify-between"
          data-animate="fade-rise"
          data-animate-active="false"
        >
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-main)]">
              Home Page Controls
            </h1>
            <p className="mt-2 text-sm sm:text-base text-muted">
              Control visibility and behavior of home page elements and features
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/settings")}
            className="px-4 py-2 rounded-xl border border-theme text-muted hover:text-[var(--text-main)] hover:border-[var(--accent)] transition-all duration-200"
          >
            Back to Settings
          </button>
        </header>

        {/* Home Page Controls */}
        <div
          data-animate="fade-scale"
          data-animate-active="false"
          className="bg-theme-elevated rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 md:p-10 border border-theme text-[var(--text-main)]"
        >
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[var(--text-main)] mb-4">
            Home Page Controls
          </h2>
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Show Public Reviews
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Control whether verified customer reviews appear on storefront
                  pages.
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.show_public_reviews}
                  aria-label="Toggle public visibility for product reviews"
                  onClick={() => handleToggle("show_public_reviews")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.show_public_reviews
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.show_public_reviews
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.show_public_reviews ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Show Home Testimonials
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Display the customer testimonials block on the home page.
                  Requires public reviews to be visible.
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={
                    formData.show_home_testimonials &&
                    formData.show_public_reviews
                  }
                  aria-label="Toggle testimonials section on home page"
                  onClick={() => handleToggle("show_home_testimonials")}
                  disabled={!formData.show_public_reviews}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.show_home_testimonials &&
                    formData.show_public_reviews
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } ${
                    !formData.show_public_reviews
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:scale-[1.02]"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.show_home_testimonials &&
                      formData.show_public_reviews
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.show_home_testimonials &&
                  formData.show_public_reviews
                    ? "ON"
                    : "OFF"}
                </span>
              </div>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Show Chef&apos;s Picks Section
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Display the Chef&apos;s Picks featured section on the menu
                  page
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.show_chefs_picks}
                  aria-label="Toggle chef's picks section on menu page"
                  onClick={() => handleToggle("show_chefs_picks")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.show_chefs_picks
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.show_chefs_picks
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.show_chefs_picks ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Show Ambience Uploader
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Toggle visibility of the ambience uploader block on the public
                  home page. Recommended to keep hidden unless updating ambience
                  assets.
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.show_home_ambience_uploader}
                  aria-label="Toggle ambience uploader visibility on home page"
                  onClick={() => handleToggle("show_home_ambience_uploader")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.show_home_ambience_uploader
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.show_home_ambience_uploader
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.show_home_ambience_uploader ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Show Theme Toggle
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Allow visitors to switch between light and dark themes from
                  the navigation bar. Disable to keep the site locked to the
                  default look.
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.show_theme_toggle}
                  aria-label="Toggle theme switch visibility in navigation"
                  onClick={() => handleToggle("show_theme_toggle")}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 ${
                    formData.show_theme_toggle
                      ? "bg-[#C59D5F] border-[#E5C990] shadow-[0_0_12px_rgba(197,157,95,0.45)]"
                      : "bg-theme-elevated border-transparent"
                  } hover:scale-[1.02]`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-all duration-200 ${
                      formData.show_theme_toggle
                        ? "translate-x-6 shadow-[0_4px_14px_rgba(197,157,95,0.35)]"
                        : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                  {formData.show_theme_toggle ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-theme-subtle pt-4">
              <div className="max-w-md">
                <p className="text-sm font-medium text-[var(--text-main)] mb-1">
                  Scroll Thumb Brightness
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Fine tune the overlay scrollbar thumb visibility across the
                  entire application. Lower values keep the slider subtle,
                  higher values make it easier to spot.
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <input
                  type="range"
                  name="scroll_thumb_brightness"
                  min="0.05"
                  max="1"
                  step="0.05"
                  value={formData.scroll_thumb_brightness ?? 0.6}
                  onChange={handleChange}
                  className="w-44 accent-[var(--accent)]"
                />
                <span className="text-xs text-[var(--text-muted)] uppercase tracking-[0.25em]">
                  {Math.round(
                    ((formData.scroll_thumb_brightness ?? 0.6) +
                      Number.EPSILON) *
                      100,
                  )}
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Spacer for sticky footer */}
        <div className="h-24" />
      </div>

      {/* Sticky Save Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${
          hasUnsavedChanges || saving || success || saveError
            ? "translate-y-0"
            : "translate-y-full"
        }`}
      >
        <div className="bg-[var(--bg-elevated)] border-t border-theme shadow-lg backdrop-blur-sm">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-4">
            <div className="flex items-center justify-between gap-4">
              {/* Status indicator */}
              <div className="flex items-center gap-3">
                {hasUnsavedChanges && !saving && !success && !saveError && (
                  <div className="flex items-center gap-2 text-amber-500">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-sm font-medium">Unsaved changes</span>
                  </div>
                )}
                {saving && (
                  <div className="flex items-center gap-2 text-[var(--accent)]">
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="text-sm font-medium">Saving...</span>
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-2 text-green-500">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Settings saved successfully!
                    </span>
                  </div>
                )}
                {saveError && (
                  <div className="flex items-center gap-2 text-red-500">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="text-sm font-medium">{saveError}</span>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={saving || !hasUnsavedChanges}
                  className="bg-theme-elevated text-[var(--text-main)] min-h-[44px] px-4 sm:px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm border border-theme hover:bg-[var(--bg-main)]"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  className="bg-[var(--accent)] text-black min-h-[44px] px-6 sm:px-8 py-2.5 rounded-xl font-semibold hover:bg-[#B08D4F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {saving ? "Saving..." : "Save All Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Discard Changes"
        message="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        confirmText="Discard"
        cancelText="Cancel"
        variant="warning"
      />
    </m.main>
  );
}

export default AdminHomePageControls;
